/**
 * feAI – Frontend
 * src/pages/ForgotPasswordPage.tsx
 *
 * Pagina "Password dimenticata" — gestisce tre step:
 *
 *   [request]   → form per inserire email/username → chiama /forgot-password
 *   [otp]       → (solo canale SMS) form per inserire OTP a 6 cifre
 *   [done]      → messaggio di conferma (email inviata / OTP verificato)
 *
 * Quando viene rilevato il flag ?force=1 in query string (redirect automatico
 * dal login con must_reset_password=TRUE) mostra un banner informativo.
 *
 * Quando viene rilevato ?channel=sms&dest=+39*** (passato da LoginPage dopo
 * MUST_RESET) salta direttamente allo step OTP.
 */

import React, { useState, useId, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FuturEnergyLogo } from '../components/brand/FuturEnergyLogo';
import { apiForgotPassword, apiVerifyOtp } from '../lib/authApi';
import '../styles/login.css';

type Step = 'request' | 'otp' | 'done';

export const ForgotPasswordPage: React.FC = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const isForced       = searchParams.get('force') === '1';
  const paramChannel   = searchParams.get('channel') as 'email' | 'sms' | null;
  const paramDest      = searchParams.get('dest');

  // ── State ──────────────────────────────────────────────────────────────────
  const [step,        setStep]        = useState<Step>(() =>
    // Se proveniamo da MUST_RESET con SMS già avviato, vai diretto allo step OTP
    paramChannel === 'sms' && paramDest ? 'otp' : 'request'
  );
  const [identificativo, setIdentificativo] = useState('');
  const [otp,          setOtp]          = useState('');
  const [channel,      setChannel]      = useState<'email' | 'sms' | null>(paramChannel);
  const [maskedDest,   setMaskedDest]   = useState<string | null>(paramDest);
  const [isLoading,    setIsLoading]    = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  const idId  = useId();
  const otpId = useId();

  // Focus automatico sull'input OTP quando si entra nello step
  useEffect(() => {
    if (step === 'otp') {
      document.getElementById(otpId)?.focus();
    }
  }, [step, otpId]);

  // ── Handler: richiesta reset ───────────────────────────────────────────────
  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!identificativo.trim()) return;

    setError(null);
    setIsLoading(true);

    try {
      const result = await apiForgotPassword(identificativo.trim());

      setChannel(result.channel);
      setMaskedDest(result.maskedDestination);

      if (result.channel === 'sms') {
        // Vai allo step OTP
        setStep('otp');
      } else {
        // Email inviata → step done
        setStep('done');
      }
    } catch (err) {
      setError((err as Error).message ?? 'Si è verificato un errore. Riprova.');
    } finally {
      setIsLoading(false);
    }
  }

  // ── Handler: verifica OTP ──────────────────────────────────────────────────
  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length !== 6) return;

    setError(null);
    setIsLoading(true);

    try {
      const result = await apiVerifyOtp(otp);
      // OTP valido → vai alla pagina reset con il token confermato
      navigate(
        `/reset-password?token=${encodeURIComponent(result.reset_token)}&channel=sms`,
        { replace: true }
      );
    } catch (err) {
      setError((err as Error).message ?? 'Codice non valido. Ricontrolla e riprova.');
      setIsLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <main className="login-page" aria-label="Reset password feAI">
      <div className="login-card">

        <div className="login-logo">
          <FuturEnergyLogo variant="positive" size={200} />
        </div>

        {/* ── Banner "cambio password obbligatorio" ──────────────────────── */}
        {isForced && step === 'request' && (
          <div
            className="error-banner"
            role="alert"
            style={{ background: 'rgba(255,180,0,0.10)', borderColor: 'rgba(255,180,0,0.40)', color: '#92600a' }}
          >
            <span aria-hidden="true">⚠</span>
            Il tuo account richiede un cambio password per accedere al nuovo portale.
          </div>
        )}

        {/* ── STEP: request ─────────────────────────────────────────────── */}
        {step === 'request' && (
          <>
            <h1 className="login-title">Reimposta password</h1>
            <p className="login-subtitle">
              Inserisci la tua email o il tuo username. Ti invieremo le istruzioni
              per scegliere una nuova password.
            </p>

            {error && (
              <div className="error-banner" role="alert">
                <span aria-hidden="true">⚠</span>{error}
              </div>
            )}

            <form onSubmit={handleRequest} noValidate>
              <div className="form-group">
                <label htmlFor={idId} className="form-label">Email o Username</label>
                <input
                  id={idId}
                  type="text"
                  className="form-input"
                  placeholder="nome@azienda.it oppure rossmar001"
                  value={identificativo}
                  onChange={(e) => setIdentificativo(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !identificativo.trim()}
              >
                {isLoading
                  ? <><span className="spinner" aria-hidden="true" /> Invio in corso…</>
                  : <>Invia istruzioni →</>
                }
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
              <Link
                to="/login"
                style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textDecoration: 'none' }}
              >
                ← Torna al login
              </Link>
            </div>
          </>
        )}

        {/* ── STEP: OTP SMS ─────────────────────────────────────────────── */}
        {step === 'otp' && (
          <>
            <div className="profile-picker-header">
              <div className="profile-picker-icon" aria-hidden="true">💬</div>
              <h1 className="login-title">Codice via SMS</h1>
              <p className="login-subtitle">
                Abbiamo inviato un codice di 6 cifre al numero
                {maskedDest ? <> <strong>{maskedDest}</strong></> : ' registrato'}.
                <br />Inseriscilo qui sotto. Scade tra 30 minuti.
              </p>
            </div>

            {error && (
              <div className="error-banner" role="alert">
                <span aria-hidden="true">⚠</span>{error}
              </div>
            )}

            <form onSubmit={handleVerifyOtp} noValidate>
              <div className="form-group">
                <label htmlFor={otpId} className="form-label">Codice OTP</label>
                <input
                  id={otpId}
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="form-input"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  required
                  disabled={isLoading}
                  style={{ letterSpacing: '0.3em', fontSize: '1.4rem', textAlign: 'center' }}
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading
                  ? <><span className="spinner" aria-hidden="true" /> Verifica…</>
                  : <>Verifica codice →</>
                }
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                className="back-link"
                onClick={() => { setStep('request'); setOtp(''); setError(null); }}
              >
                ← Non hai ricevuto il codice? Riprova
              </button>
            </div>
          </>
        )}

        {/* ── STEP: done (email inviata) ────────────────────────────────── */}
        {step === 'done' && (
          <>
            <div className="profile-picker-header">
              <div className="profile-picker-icon" aria-hidden="true">✉️</div>
              <h1 className="login-title">Email inviata</h1>
              <p className="login-subtitle">
                {maskedDest
                  ? <>Abbiamo inviato il link di reset a <strong>{maskedDest}</strong>.</>
                  : <>Abbiamo inviato le istruzioni di reset al tuo indirizzo email.</>
                }
                <br /><br />
                Controlla la casella di posta (e la cartella spam).
                Il link è valido per <strong>30 minuti</strong>.
              </p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <Link
                to="/login"
                className="btn-primary"
                style={{ display: 'inline-flex', textDecoration: 'none', justifyContent: 'center' }}
              >
                Torna al login
              </Link>
            </div>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                className="back-link"
                onClick={() => { setStep('request'); setError(null); setIdentificativo(''); }}
              >
                Non hai ricevuto l'email? Riprova
              </button>
            </div>
          </>
        )}

      </div>
    </main>
  );
};
